// Extracted from original/src/components/CustomerReceiptVoucher.tsx:477-556 (`CustomerReceiptVoucherModal`: the window
// that showed the entry voucher with its five buttons), ADR-08 §2.1. Kept: backdrop, card, header with the FileText
// icon and title, the grey preview well, the footer with close / WhatsApp / share PDF / export PDF / print in the old
// order and colours. Cut: `printElement` / `generatePDF` / `navigator.share` / `window.open(wa.me)` → callbacks (the
// container prints through @faylasuf/print, records the issue on the server first — D-147 — and builds the WhatsApp
// link, D-063); the voucher itself arrives as `children` (the @faylasuf/print template). [C28] D-272.
import { Download, FileText, MessageCircle, Printer, Share2, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useT } from '../i18n-context.js';

export interface CustomerVoucherModalProps {
  readonly children: ReactNode;
  readonly busy: boolean;
  readonly onClose: () => void;
  readonly onWhatsApp: () => void;
  readonly onSharePdf: () => void;
  readonly onExportPdf: () => void;
  readonly onPrint: () => void;
  /** [C35] the same window for the installment receipt and the store invoice (D-054: the dialog is always shown). */
  readonly title?: string;
  /** [C35] D-149: the format of this copy (office default per voucher type, switchable here); absent = A4 only. */
  readonly format?: 'a4' | '80mm';
  readonly onFormatChange?: (f: 'a4' | '80mm') => void;
}

export function CustomerVoucherModal(props: CustomerVoucherModalProps) {
  const t = useT();
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 pb-16 sm:pb-4 bg-black/60 backdrop-blur-md overflow-hidden" data-testid="customer-voucher-modal">
      <div className="relative bg-white dark:bg-[#0d1117] w-full max-w-3xl rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh] sm:max-h-[88vh] border border-gray-200 dark:border-[#30363d]">
        {/* Modal Header */}
        <div className="p-5 border-b border-gray-200 dark:border-[#30363d] flex justify-between items-center bg-gray-50 dark:bg-[#05070a]">
          <div className="flex items-center gap-2">
            <FileText className="text-blue-600 dark:text-blue-400" size={20} />
            <h3 className="text-lg font-black text-gray-900 dark:text-white">{props.title ?? t('print.entry.modal_title')}</h3>
          </div>
          <button onClick={props.onClose} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-white rounded-full hover:bg-gray-200 dark:hover:bg-[#161b22] transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Voucher Preview Content */}
        <div className="p-6 overflow-y-auto max-h-[70vh] bg-gray-100 dark:bg-[#05070a]">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200">{props.children}</div>
        </div>

        {/* Modal Actions Footer */}
        <div className="p-5 border-t border-gray-200 dark:border-[#30363d] bg-gray-50 dark:bg-[#05070a] flex flex-wrap gap-2.5 justify-end">
          {/* [C35] D-149 per-copy paper format (new element, V-96) — the old toggle style of the Settings cards */}
          {props.format && props.onFormatChange && (
            <div className="flex bg-gray-100 dark:bg-[#05070a] p-1 rounded-lg border border-gray-200 dark:border-[#30363d] me-auto" data-testid="voucher-format">
              {(['a4', '80mm'] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => props.onFormatChange!(f)}
                  data-testid={`voucher-format-${f}`}
                  className={`py-1.5 px-3 rounded-md text-[10px] font-black transition-all cursor-pointer ${props.format === f ? 'bg-blue-600 text-white shadow-xs' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-[#f0f6fc]'}`}
                >
                  {t(`print.format.${f}`)}
                </button>
              ))}
            </div>
          )}
          <button onClick={props.onClose} className="px-4 py-2.5 rounded-xl border border-gray-300 dark:border-[#30363d] text-gray-600 dark:text-gray-300 font-bold text-xs hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
            {t('common.close')}
          </button>
          <button
            onClick={props.onWhatsApp}
            disabled={props.busy}
            className="px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs transition-all shadow-lg shadow-emerald-600/20 flex items-center gap-1.5 disabled:opacity-50"
            title={t('print.entry.whatsapp_title')}
          >
            <MessageCircle size={15} />
            {t('print.entry.whatsapp')}
          </button>
          <button
            onClick={props.onSharePdf}
            disabled={props.busy}
            className="px-4 py-2.5 rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-black text-xs transition-all shadow-lg shadow-teal-600/20 flex items-center gap-1.5 disabled:opacity-50"
            title={t('print.entry.share_title')}
          >
            <Share2 size={15} />
            {t('print.entry.share_pdf')}
          </button>
          <button
            onClick={props.onExportPdf}
            disabled={props.busy}
            className="px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-black text-xs transition-all shadow-lg shadow-indigo-600/20 flex items-center gap-1.5 disabled:opacity-50"
            title={t('print.entry.export_title')}
          >
            <Download size={15} />
            {t('print.entry.export_pdf')}
          </button>
          <button
            onClick={props.onPrint}
            disabled={props.busy}
            data-testid="customer-voucher-print"
            className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-black text-xs transition-all shadow-lg shadow-blue-600/20 flex items-center gap-1.5 disabled:opacity-50"
            title={t('print.entry.print_title')}
          >
            <Printer size={15} />
            {t('print.entry.print')}
          </button>
        </div>
      </div>
    </div>
  );
}
